import {
  FoodItem,
  ICategoriesApi,
  FoodItemInSubcategoryResponse,
} from '../types';
import { stringifyParameters } from '../helpers';
import { instance } from './instance';
import {
  ProductGroup,
  UpdateCategoryPayload,
  CreateDepartmentPayload,
  CreateSubcategoryPayload,
  ICreateCategory,
  TypeSubCategoryInclude,
  CreateProductGroupPayload,
  UpdateSubcategoryPayload,
} from '../types/categories.types';

export const categoriesApi: ICategoriesApi = {
  getAllCategories: async (query?: any) => {
    const { data } = await instance.get('/category/all/', { params: query });
    return data;
  },
  getCategory: async (slug = 'all') =>
    (await instance.get(`/category/${slug}/`)).data.data,
  getBrandCategories: async (brandId: string | number) =>
    await instance.get(`/category/brand/${brandId}`),
  createCategory: async (payload) =>
    await instance.post('/admin/category/create/', payload),
  updateCategory: async (payload, id) =>
    await instance.put(`/admin/category/${id}`, payload),

  rankCategory: async (payload) =>
    await instance.post(`/admin/category/rank`, payload),

  updateCategoryRank: async (payload, slug) =>
    await instance.put(`/admin/category/rank/${slug}`, payload),

  removeCategory: async (slug) =>
    await instance.delete(`/admin/category/rank/${slug}`),

  bulkDeleteCategories: async (ids) =>
    await instance.delete(`/tenant-admin/category/bulk`, { data: { ids } }),

  marketingCategories: async () => await instance.get(`/category/marketing`),

  setMarketingCategories: async (payload) =>
    await instance.put(`/category/marketing`, payload),

  getDepartmentById: async (id: string) => {
    const { data } = await instance.get(`/department/${id}`);
    return data.data;
  },

  getSubcategoryById: async (id: number) => {
    const { data } = await instance.get(`/category/sub/${id}`);
    return data.data;
  },

  getCategoryById: async (categoryId: number) => {
    const { data } = await instance.get(`/category/id/${categoryId}`);
    return data.data;
  },

  getCollectionById: async (collectionId: string) => {
    const { data } = await instance.get(
      `/food-item/variants/groups/${collectionId}`
    );
    return data.data;
  },

  getCollections: async (params?: {
    name?: string;
    page?: number;
    count?: number;
  }) => {
    const { data } = await instance.get(`/food-item/variants/groups`, {
      params: {
        type: 'COLLECTION',
        ...(params?.name ? { name: params.name } : {}),
        ...(params?.page ? { page: params.page } : {}),
        ...(params?.count ? { count: params.count } : {}),
      },
    });
    return data;
  },
};
export const delete_collection = async (
  collectionId: string
): Promise<void> => {
  await instance.delete(`/food-item/variants/groups/${collectionId}`);
};

export const create_collection = async (params: any): Promise<ProductGroup> => {
  const payload = { ...params, type: 'COLLECTION' };
  const { data } = await instance.post(`/food-item/variants/groups`, payload);
  return data.data;
};

export const create_subcategory = async (
  payload: CreateSubcategoryPayload
): Promise<any> => {
  const { data } = await instance.post(`/category/sub`, payload);
  return data.data;
};

export const create_food_category = async (params: ICreateCategory) => {
  const { data } = await instance.post(`/category/create`, params);
  return data.data;
};

export const create_department = async (params: CreateDepartmentPayload) => {
  const { data } = await instance.post(`/department`, params);
  return data.data;
};

export const update_department = async (
  id: string,
  params: CreateDepartmentPayload
): Promise<any> => {
  const { data } = await instance.put(`/department/${id}`, params);
  return data.data;
};

export const delete_department = async (id: string): Promise<void> => {
  await instance.delete(`/department/${id}/hard`);
};

export const update_food_category = async (
  categoryId: number,
  params: UpdateCategoryPayload
): Promise<any> => {
  const { data } = await instance.put(`/category/${categoryId}`, params);
  return data.data;
};

export const create_product_brand = async (params: {
  name: string;
}): Promise<ProductGroup> => {
  const payload = { ...params, type: 'PRODUCT_BRAND' };
  const { data } = await instance.post(`/food-item/variants/groups`, payload);
  return data.data;
};

export const create_product_group = async (
  params: CreateProductGroupPayload
): Promise<ProductGroup> => {
  const { data } = await instance.post(`/food-item/variants/groups`, params);
  return data.data;
};

export const update_product_group = async (
  groupId: string,
  params: CreateProductGroupPayload
): Promise<ProductGroup> => {
  const { data } = await instance.put(
    `/food-item/variants/groups/${groupId}`,
    params
  );
  return data.data;
};

export const update_collection = async (
  collectionId: string,
  params: CreateProductGroupPayload
): Promise<ProductGroup> => {
  const { data } = await instance.put(
    `/food-item/variants/groups/${collectionId}`,
    params
  );
  return data.data;
};

export const get_product_groups = async (
  params?: CreateProductGroupPayload
): Promise<ProductGroup[]> => {
  const searchParams = new URLSearchParams();
  if (params?.type) {
    searchParams.append('type', params.type);
  }
  if (params?.name) {
    searchParams.append('name', params.name);
  }
  const queryString = searchParams.toString();
  const { data } = await instance.get(
    `/food-item/variants/groups${queryString ? `?${queryString}` : ''}`
  );
  return data.data;
};

export const get_subcategories_with_filters = async (
  params?: any
): Promise<TypeSubCategoryInclude[]> => {
  const payload = stringifyParameters(params);
  const { data } = await instance.get(`/category/sub${payload}`);
  return data.data;
};

export const get_subcategory_food_items = async (
  id: number
): Promise<FoodItemInSubcategoryResponse> => {
  const { data } = await instance.get(`/category/sub/${id}/food-items`);
  return data.data;
};

export const update_subcategory = async (
  id: number,
  payload: UpdateSubcategoryPayload
): Promise<TypeSubCategoryInclude> => {
  const { data } = await instance.put(`/category/sub/${id}`, payload);
  return data.data;
};

export const get_departments = async (params?: {
  page?: number;
  count?: number;
}) => {
  const { data } = await instance.get(`/department`, { params });
  return data;
};

export const list_variant_groups = async (): Promise<ProductGroup[]> => {
  const { data } = await instance.get('/food-item/variants/groups');
  return data.data;
};

export const list_all_products = async (): Promise<any> => {
  const { data } = await instance.get(`/food-item`);
  return data;
};

export const get_products_by_variant_groups = async (
  groupIds: string[]
): Promise<FoodItem[]> => {
  const { data } = await instance.post(`/food-item/variants/groups/items`, {
    groupIds,
  });
  return data.data;
};
